import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { BatchDetail, WorkOrderListItem } from './types';

// Hooks du detail de fournee et de l'affectation des OF (lot 5).

export function useWorkOrders(filter: { assigned?: boolean; search?: string } = {}) {
  const params = new URLSearchParams();
  if (filter.assigned !== undefined) params.set('assigned', String(filter.assigned));
  if (filter.search) params.set('search', filter.search);
  const qs = params.toString();
  return useQuery({
    queryKey: ['work-orders', filter.assigned ?? null, filter.search ?? ''],
    queryFn: () => api.get<WorkOrderListItem[]>(`/work-orders${qs ? `?${qs}` : ''}`),
  });
}

export function useBatchDetail(batchId: number | null) {
  return useQuery({
    queryKey: ['batch-detail', batchId],
    queryFn: () => api.get<BatchDetail>(`/batches/${batchId}/detail`),
    enabled: batchId != null,
  });
}

/**
 * Détails de plusieurs fournées en parallèle (compteurs + alertes), pour la
 * prévisualisation de faisabilité de la vue fusionnée. Renvoie une carte
 * batchId -> BatchDetail (les résultats non encore chargés sont absents).
 */
export function useBatchDetails(batchIds: number[]): Map<number, BatchDetail> {
  const results = useQueries({
    queries: batchIds.map((id) => ({
      queryKey: ['batch-detail', id],
      queryFn: () => api.get<BatchDetail>(`/batches/${id}/detail`),
    })),
  });
  const map = new Map<number, BatchDetail>();
  results.forEach((r) => {
    if (r.data) map.set(r.data.id, r.data);
  });
  return map;
}

/** Invalide les vues touchees par une (dés)affectation d'OF. */
function useInvalidateAssignment() {
  const qc = useQueryClient();
  return (batchId: number) =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['work-orders'] }),
      qc.invalidateQueries({ queryKey: ['batch-detail', batchId] }),
      qc.invalidateQueries({ queryKey: ['planning'] }),
    ]);
}

export function useAssignOrder() {
  const invalidate = useInvalidateAssignment();
  return useMutation({
    mutationFn: (input: { batchId: number; codeOf: string }) =>
      api.post(`/batches/${input.batchId}/items`, { workOrderCodeOf: input.codeOf }),
    onSuccess: (_data, input) => invalidate(input.batchId),
  });
}

export function useUnassignOrder() {
  const invalidate = useInvalidateAssignment();
  return useMutation({
    mutationFn: (input: { batchId: number; codeOf: string }) =>
      api.del(`/batches/${input.batchId}/items/${encodeURIComponent(input.codeOf)}`),
    onSuccess: (_data, input) => invalidate(input.batchId),
  });
}
